/**
 * Chat Timing Utility
 * Tracks timing across the entire chat message processing pipeline
 * to identify latency bottlenecks.
 */

export interface TimingMark {
  name: string;
  time: number;
  relativeTime: number; // Time relative to start
}

export interface TimingStage {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
}

export type TimingFlagValue = boolean | number | string;

export interface TimingSummary {
  requestId: string;
  startTime: number;
  marks: TimingMark[];
  stages: TimingStage[];
  flags?: Record<string, TimingFlagValue>;
  totalDuration: number;
}

export class ChatTiming {
  private requestId: string;
  private startTime: number;
  private marks: TimingMark[] = [];
  private stages: Map<string, TimingStage> = new Map();
  private flags: Record<string, TimingFlagValue> = {};
  private isBrowser: boolean;

  /**
   * Attach an arbitrary flag to the current request timing (e.g. KV hit/miss
   * or whether a given cache was populated). Flags are persisted through
   * serialize/deserialize so they show up in the cross-service summary.
   */
  setFlag(name: string, value: TimingFlagValue): void {
    this.flags[name] = value;
  }

  getFlag(name: string): TimingFlagValue | undefined {
    return this.flags[name];
  }

  getFlags(): Record<string, TimingFlagValue> {
    return { ...this.flags };
  }

  constructor(requestId?: string) {
    this.requestId = requestId || this.generateRequestId();
    this.isBrowser = typeof window !== 'undefined';
    
    // Use performance.now() in browser, Date.now() in Node.js
    if (this.isBrowser) {
      this.startTime = performance.now();
    } else {
      // Use high-resolution time in Node.js
      const hrtime = process.hrtime.bigint();
      this.startTime = Number(hrtime) / 1_000_000; // Convert to milliseconds
    }
    
    this.mark('timing_start');
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private getCurrentTime(): number {
    if (this.isBrowser) {
      return performance.now();
    } else {
      // Use high-resolution time in Node.js
      if (typeof process !== 'undefined' && process.hrtime && typeof process.hrtime.bigint === 'function') {
        const hrtime = process.hrtime.bigint();
        return Number(hrtime) / 1_000_000; // Convert nanoseconds to milliseconds
      } else {
        // Fallback to Date.now() if hrtime.bigint is not available
        return Date.now();
      }
    }
  }

  /**
   * Mark a point in time
   */
  mark(markerName: string): void {
    const currentTime = this.getCurrentTime();
    const relativeTime = currentTime - this.startTime;
    
    const mark: TimingMark = {
      name: markerName,
      time: currentTime,
      relativeTime: Math.round(relativeTime * 100) / 100 // Round to 0.01ms
    };
    
    this.marks.push(mark);
    
    // Log immediately for real-time visibility
    this.logMark(mark);
  }

  /**
   * Start timing a stage
   */
  start(stageName: string): void {
    const currentTime = this.getCurrentTime();
    
    const stage: TimingStage = {
      name: stageName,
      startTime: currentTime
    };
    
    this.stages.set(stageName, stage);
    this.mark(`${stageName}_start`);
  }

  /**
   * End timing a stage and log duration
   */
  end(stageName: string): void {
    const stage = this.stages.get(stageName);
    if (!stage) {
      // Only warn if LOG_TIMING is enabled (to reduce noise)
      if (process.env.LOG_TIMING === 'true') {
        console.warn(`[CHAT_TIMING] Stage "${stageName}" was not started`);
      }
      return;
    }
    
    const currentTime = this.getCurrentTime();
    const duration = currentTime - stage.startTime;
    
    stage.endTime = currentTime;
    stage.duration = Math.round(duration * 100) / 100; // Round to 0.01ms
    
    this.mark(`${stageName}_end`);
    this.logStage(stage);
  }

  /**
   * Time an async operation
   */
  async time<T>(stageName: string, operation: () => Promise<T>): Promise<T> {
    this.start(stageName);
    try {
      const result = await operation();
      this.end(stageName);
      return result;
    } catch (error) {
      this.end(stageName);
      throw error;
    }
  }

  /**
   * Time a synchronous operation
   */
  timeSync<T>(stageName: string, operation: () => T): T {
    this.start(stageName);
    try {
      const result = operation();
      this.end(stageName);
      return result;
    } catch (error) {
      this.end(stageName);
      throw error;
    }
  }

  private logMark(mark: TimingMark): void {
    // Only log if LOG_TIMING environment variable is set to 'true'
    if (process.env.LOG_TIMING === 'true') {
      console.log(
        `[CHAT_TIMING] Request ID: ${this.requestId} | Mark: ${mark.name} | Time: ${mark.relativeTime.toFixed(2)}ms`
      );
    }
  }

  private logStage(stage: TimingStage): void {
    // Only log if LOG_TIMING environment variable is set to 'true'
    if (process.env.LOG_TIMING === 'true' && stage.duration !== undefined) {
      console.log(
        `[CHAT_TIMING] Request ID: ${this.requestId} | Stage: ${stage.name} | Duration: ${stage.duration.toFixed(2)}ms`
      );
    }
  }

  /**
   * Get timing summary
   */
  getSummary(): TimingSummary {
    const currentTime = this.getCurrentTime();
    const totalDuration = currentTime - this.startTime;
    
    return {
      requestId: this.requestId,
      startTime: this.startTime,
      marks: [...this.marks],
      stages: Array.from(this.stages.values()),
      flags: { ...this.flags },
      totalDuration: Math.round(totalDuration * 100) / 100
    };
  }

  /**
   * Log complete timing breakdown
   * Only logs if LOG_TIMING environment variable is set to 'true'
   */
  logSummary(): void {
    // Only log if LOG_TIMING environment variable is set to 'true'
    if (process.env.LOG_TIMING !== 'true') {
      return;
    }

    try {
      const summary = this.getSummary();
      
      // Add defensive checks and logging
      if (!summary) {
        console.error('[CHAT_TIMING] ERROR: getSummary() returned undefined/null');
        return;
      }
      
      if (typeof summary.totalDuration === 'undefined' || summary.totalDuration === null) {
        console.error('[CHAT_TIMING] ERROR: summary.totalDuration is undefined/null', {
          summaryKeys: Object.keys(summary),
          summaryType: typeof summary,
          hasStages: 'stages' in summary,
          hasMarks: 'marks' in summary,
          stages: summary.stages,
          marks: summary.marks
        });
        return;
      }
      
      console.log(`[CHAT_TIMING] ===== TIMING SUMMARY =====`);
      console.log(`[CHAT_TIMING] Request ID: ${summary.requestId}`);
      console.log(`[CHAT_TIMING] Total Duration: ${summary.totalDuration.toFixed(2)}ms`);
    console.log(`[CHAT_TIMING] `);
    console.log(`[CHAT_TIMING] Stages:`);
    
    // Sort stages by start time
    const sortedStages = summary.stages
      .filter(s => s.duration !== undefined)
      .sort((a, b) => a.startTime - b.startTime);
    
    sortedStages.forEach(stage => {
      const percentage = summary.totalDuration > 0
        ? ((stage.duration! / summary.totalDuration) * 100).toFixed(1)
        : '0.0';
      console.log(
        `[CHAT_TIMING]   ${stage.name}: ${stage.duration!.toFixed(2)}ms (${percentage}%)`
      );
    });
    
    console.log(`[CHAT_TIMING] `);
    console.log(`[CHAT_TIMING] Key Marks:`);
    summary.marks.forEach(mark => {
      console.log(
        `[CHAT_TIMING]   ${mark.name}: ${mark.relativeTime.toFixed(2)}ms`
      );
    });

    if (summary.flags && Object.keys(summary.flags).length > 0) {
      console.log(`[CHAT_TIMING] `);
      console.log(`[CHAT_TIMING] Flags:`);
      Object.entries(summary.flags).forEach(([name, value]) => {
        console.log(`[CHAT_TIMING]   ${name}: ${String(value)}`);
      });
    }
    
    // Identify bottleneck
    if (sortedStages.length > 0) {
      const bottleneck = sortedStages.reduce((max, stage) => 
        (stage.duration || 0) > (max.duration || 0) ? stage : max
      );
      const percentage = summary.totalDuration > 0
        ? ((bottleneck.duration! / summary.totalDuration) * 100).toFixed(1)
        : '0.0';
      console.log(`[CHAT_TIMING] `);
      console.log(
        `[CHAT_TIMING] Bottleneck: ${bottleneck.name} (${bottleneck.duration!.toFixed(2)}ms, ${percentage}%)`
      );
    }
    
      console.log(`[CHAT_TIMING] =========================`);
    } catch (error: any) {
      console.error('[CHAT_TIMING] ERROR in logSummary()', {
        error: error.message,
        errorName: error.name,
        stack: error.stack,
        errorString: String(error),
        errorKeys: error && typeof error === 'object' ? Object.keys(error) : []
      });
      throw error; // Re-throw to be caught by caller
    }
  }

  /**
   * Get request ID for passing between services
   */
  getRequestId(): string {
    return this.requestId;
  }

  /**
   * Serialize timing data for headers
   */
  serialize(): string {
    return JSON.stringify({
      requestId: this.requestId,
      startTime: this.startTime,
      marks: this.marks,
      stages: Array.from(this.stages.values()),
      flags: this.flags,
    });
  }

  /**
   * Deserialize timing data from headers
   */
  static deserialize(data: string): Partial<TimingSummary> {
    try {
      return JSON.parse(data);
    } catch (error) {
      // Only warn if LOG_TIMING is enabled
      if (process.env.LOG_TIMING === 'true') {
        console.warn('[CHAT_TIMING] Failed to deserialize timing data:', error);
      }
      return {};
    }
  }

  /**
   * Create timing instance from serialized data
   * 
   * IMPORTANT: When restoring timing across services (frontend -> backend),
   * we need to handle clock differences. The frontend uses performance.now()
   * (relative to page load, small numbers), while backend uses hrtime or Date.now()
   * (which can be huge numbers).
   * 
   * Strategy: Preserve the relativeTime values from frontend marks, but reset
   * startTime to "now" on the backend. Then adjust the startTime backwards by
   * the elapsed time from the last frontend mark, so new marks continue the timeline.
   */
  static fromSerialized(data: string): ChatTiming {
    const parsed = this.deserialize(data);
    const timing = new ChatTiming(parsed.requestId);
    
    // Get the backend's current time
    const backendNow = timing.getCurrentTime();
    
    // Find the last frontend mark to know how much time has elapsed
    let maxRelativeTime = 0;
    if (parsed.marks && parsed.marks.length > 0) {
      maxRelativeTime = Math.max(...parsed.marks.map((m: TimingMark) => m.relativeTime || 0));
    }
    
    // Reset startTime to be relative to backend's "now", adjusted backwards by elapsed time
    // This ensures new marks continue from where frontend left off
    timing.startTime = backendNow - maxRelativeTime;
    
    // Restore all marks, preserving their relativeTime values
    // Recalculate absolute times based on the new startTime
    if (parsed.marks) {
      timing.marks = parsed.marks.map((mark: TimingMark) => ({
        name: mark.name,
        time: timing.startTime + (mark.relativeTime || 0),
        relativeTime: mark.relativeTime || 0
      }));
    }
    
    // Restore stages (these use durations, so they're fine as-is)
    if (parsed.stages) {
      parsed.stages.forEach((stage: TimingStage) => {
        timing.stages.set(stage.name, stage);
      });
    }

    // Restore flags if present in the serialized payload.
    if ((parsed as any).flags && typeof (parsed as any).flags === 'object') {
      timing.flags = { ...((parsed as any).flags as Record<string, TimingFlagValue>) };
    }

    return timing;
  }
}

